import { Middleware } from "@reduxjs/toolkit";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import {
    IOptionCode,
    IOptionValue,
    IOptionValues,
    OptionValue,
} from "../../models/catalogue";
import { check } from "../../models/utils";
import { setOptionValue } from "./reducers";

const getOptionKey = (namespace: string, code: string) => {
    return `configurator-option-${namespace}-${code}`;
};

export const persistOptionValue = (
    namespace: string,
    code: string,
    value: IOptionValue,
): void => {
    const window = getWindow();
    if (!window) {
        return;
    }
    const key = getOptionKey(namespace, code);
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        console.debug("Cannot write to localStorage");
    }
};

const retrieveOptionValue = (
    namespace: string,
    code: string,
): IOptionValue | null => {
    const window = getWindow();
    if (!window) {
        return null;
    }
    const key = getOptionKey(namespace, code);
    try {
        const val = window.localStorage.getItem(key);
        return val ? check(OptionValue.decode(JSON.parse(val))) : null;
    } catch (e) {
        console.debug("Cannot read from localStorage");
        return null;
    }
};

/**
 * Persist option selection values to localStorage so that they can be
 * automatically selected upon the next page load.
 */
export const persistOptionSelections: Middleware = () => {
    return (next) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (action: any) => {
            if (action.type === setOptionValue.type) {
                persistOptionValue(
                    action.payload.namespace,
                    action.payload.code,
                    action.payload.value,
                );
            }
            return next(action);
        };
    };
};

/**
 * Retrieve the last selected values for the given namespace and option codes.
 */
export const getPreferredOptionSelections = (
    namespace: string,
    codes: IOptionCode[],
) => {
    const values: IOptionValues = {};
    codes.forEach((code) => {
        const value = retrieveOptionValue(namespace, code);
        if (value) {
            values[code] = value;
        }
    });
    return values;
};
